import { useState, useMemo } from 'react';
import { UserSidebar } from '@/components/Layout/UserSidebar';
import { useTheme } from '@/hooks/useTheme';
import { ProjectLogo } from '@/components/ProjectLogo';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectService } from '@/services/projectService';
import { projectDocumentService, ProjectDocument } from '@/services/projectDocumentService';
import { useAuthStore } from '@/store/authStore';
import { useNavigate, useParams } from 'react-router-dom';

export function DocumentationsPage() {
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const projectIdInt = projectId ? Number(projectId) : null;
  const { user, isRoot } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  // Fetch user's projects to get current project
  const { data: projectsResponse } = useQuery({
    queryKey: ['user-projects'],
    queryFn: () => projectService.getProjects({ page: 1, itemsPerPage: 100 }),
  });

  const userProjects = useMemo(() => {
    return projectsResponse?.data || [];
  }, [projectsResponse]);

  const currentProject = useMemo(() => {
    if (!projectIdInt) return null;
    return userProjects.find((p) => p.id === projectIdInt) ?? null;
  }, [userProjects, projectIdInt]);

  const { data: myRole } = useQuery({
    queryKey: ['project-my-role', projectIdInt],
    queryFn: () => projectService.getMyProjectRole(projectIdInt as number),
    enabled: typeof projectIdInt === 'number' && Number.isFinite(projectIdInt),
    retry: 1,
  });

  // Check if user can upload/delete (PROJECT_ADMIN, PROJECT_MANAGER, or ROLE_ROOT)
  const canManageDocuments = useMemo(() => {
    if (isRoot()) return true;
    const role = myRole?.role ?? null;
    return role === 'PROJECT_ADMIN' || role === 'PROJECT_MANAGER';
  }, [isRoot, myRole?.role]);

  // Fetch documents for current project
  const { data: documents = [], isLoading: isLoadingDocuments } = useQuery({
    queryKey: ['project-documents', projectIdInt],
    queryFn: () => projectDocumentService.getDocuments(projectIdInt as number),
    enabled: typeof projectIdInt === 'number' && Number.isFinite(projectIdInt),
    retry: 1,
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: (file: File) => projectDocumentService.uploadDocument(projectIdInt!, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-documents', projectIdInt] });
      setSelectedFile(null);
      setIsUploading(false);
      const fileInput = document.getElementById('file-input') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    },
    onError: (error: any) => {
      setIsUploading(false);
      alert(error?.response?.data?.error || 'Erreur lors de l\'upload du document');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (documentId: number) => projectDocumentService.deleteDocument(projectIdInt!, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-documents', projectIdInt] });
      setDeleteConfirmId(null);
    },
    onError: (error: any) => {
      alert(error?.response?.data?.error || 'Erreur lors de la suppression du document');
    },
  });

  if (!projectIdInt) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <span className="material-icons-outlined text-red-500 text-6xl mb-4">error</span>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Projet requis</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-4">Veuillez sélectionner un projet pour accéder aux documentations.</p>
            <button
              onClick={() => navigate('/dashboard', { replace: true })}
              className="px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              Choisir un projet
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!extension || !allowedExtensions.includes(extension)) {
        alert('Type de fichier non autorisé. Formats acceptés : PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX');
        return;
      }
      // Validate file size (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        alert('Le fichier est trop volumineux. Taille maximale : 50MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !projectIdInt) {
      alert('Veuillez sélectionner un fichier');
      return;
    }

    setIsUploading(true);
    uploadMutation.mutate(selectedFile);
  };

  const handleDownload = async (doc: ProjectDocument) => {
    try {
      const blob = await projectDocumentService.downloadDocument(projectIdInt!, doc.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erreur lors du téléchargement du document';
      alert(message);
    }
  };

  const handleDelete = (documentId: number) => {
    deleteMutation.mutate(documentId);
  };

  const getFileIcon = (mimeType: string): string => {
    if (mimeType.includes('pdf')) return 'picture_as_pdf';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'description';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'table_chart';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'slideshow';
    return 'description';
  };

  return (
    <div className="bg-slate-50 text-slate-900 dark:bg-slate-900 dark:text-slate-100 h-screen flex overflow-hidden font-display">
      {/* Sidebar */}
      <UserSidebar projectId={projectIdInt} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative bg-slate-50 dark:bg-slate-900">
        {/* Header */}
        <header className="h-16 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-6 sm:px-8 z-20 shadow-sm sticky top-0">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <span className="material-icons-outlined" style={{ fontSize: '20px' }}>menu</span>
            </button>
            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
              Documentations
            </h1>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 mx-2"></div>
            <div className="flex items-center text-sm text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800/50 px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-700/50">
              <span className="material-icons-outlined mr-2 text-slate-500 dark:text-slate-500" style={{ fontSize: '16px' }}>domain</span>
              {currentProject?.title} • {currentProject?.city || 'Paris'}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="hidden md:flex items-center px-3 py-1 bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-full text-xs font-medium text-slate-600 dark:text-slate-300">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse shadow-sm shadow-green-500/50"></span>
              Système en ligne
            </div>
            <div className="h-8 w-px bg-slate-200 dark:bg-slate-700 mx-1"></div>
            <ProjectLogo project={currentProject} size="md" className="rounded-lg" />
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? <span className="material-icons-outlined">light_mode</span> : <span className="material-icons-outlined">dark_mode</span>}
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Upload Form - Only visible for ADMIN/MANAGER */}
            {canManageDocuments && (
              <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6 shadow-sm">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Uploader un document</h2>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="file-input" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Sélectionner un fichier
                    </label>
                    <input
                      id="file-input"
                      type="file"
                      onChange={handleFileSelect}
                      className="block w-full text-sm text-slate-500 dark:text-slate-400
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-lg file:border-0
                        file:text-sm file:font-semibold
                        file:bg-primary/20 file:text-primary
                        hover:file:bg-primary/30
                        file:cursor-pointer
                        cursor-pointer
                        border border-slate-300 dark:border-slate-600 rounded-lg
                        bg-white dark:bg-slate-700"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Formats acceptés : PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX • Taille max : 50MB
                    </p>
                  </div>

                  {selectedFile && (
                    <div className="p-3 bg-slate-100 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="material-icons-outlined text-primary" style={{ fontSize: '24px' }}>
                            description
                          </span>
                          <div>
                            <p className="text-sm font-medium text-slate-900 dark:text-white">{selectedFile.name}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">{formatFileSize(selectedFile.size)}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedFile(null);
                            const fileInput = document.getElementById('file-input') as HTMLInputElement;
                            if (fileInput) {
                              fileInput.value = '';
                            }
                          }}
                          className="p-1 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                        >
                          <span className="material-icons-outlined" style={{ fontSize: '20px' }}>close</span>
                        </button>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleUpload}
                    disabled={!selectedFile || isUploading}
                    className={`w-full px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                      !selectedFile || isUploading
                        ? 'bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed'
                        : 'bg-primary text-white hover:bg-primary/90'
                    }`}
                  >
                    {isUploading ? (
                      <>
                        <span className="material-icons-outlined animate-spin" style={{ fontSize: '18px' }}>progress_activity</span>
                        <span>Upload en cours...</span>
                      </>
                    ) : (
                      <>
                        <span className="material-icons-outlined" style={{ fontSize: '18px' }}>cloud_upload</span>
                        <span>Uploader</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Documents List */}
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Documents disponibles</h2>
              
              {isLoadingDocuments ? (
                <div className="text-center py-8">
                  <span className="material-icons-outlined animate-spin text-primary text-4xl mb-4 block">progress_activity</span>
                  <p className="text-slate-500 dark:text-slate-400">Chargement des documents...</p>
                </div>
              ) : documents.length === 0 ? (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <span className="material-icons-outlined text-6xl mb-4 block">folder_open</span>
                  <p>Aucun document disponible</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                            <span className="material-icons-outlined text-primary" style={{ fontSize: '24px' }}>
                              {getFileIcon(doc.mimeType)}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{doc.originalName}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-slate-500 dark:text-slate-400">{formatFileSize(doc.size)}</span>
                            <span className="text-xs text-slate-400">•</span>
                            <span className="text-xs text-slate-500 dark:text-slate-400">{formatDate(doc.createdAt)}</span>
                            <span className="text-xs text-slate-400">•</span>
                            <span className="text-xs text-slate-500 dark:text-slate-400">Par {doc.uploadedBy}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleDownload(doc)}
                          className="p-2 rounded-lg text-slate-400 hover:text-primary hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                          title="Télécharger"
                        >
                          <span className="material-icons-outlined" style={{ fontSize: '20px' }}>download</span>
                        </button>
                        {canManageDocuments && (
                          <button
                            onClick={() => setDeleteConfirmId(doc.id)}
                            className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                            title="Supprimer"
                          >
                            <span className="material-icons-outlined" style={{ fontSize: '20px' }}>delete</span>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 max-w-md w-full border border-slate-200 dark:border-slate-700">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Supprimer le document ?</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              Cette action est irréversible. Le document sera définitivement supprimé.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteMutation.isPending ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
